import React, { useEffect, useState } from "react";
import { Button, Card, Form, Row, Col, Alert, Badge } from "react-bootstrap";
import { useSearchParams, useNavigate } from "react-router-dom";
import refundApi from "../../services/refundApi";
import omsApi from "../../services/omsapi";

export default function RefundCreatePage() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();

  const qpOrderId = sp.get("orderId") || "";

  const [loading, setLoading] = useState(false);

  // Order selector
  const [selectedOrderId, setSelectedOrderId] = useState(qpOrderId);

  // Data
  const [order, setOrder] = useState(null);     // OMS order details
  const [details, setDetails] = useState(null); // refundApi (txn, refunds)

  // Form
  const [refundType, setRefundType] = useState("FULL");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("{}");

  const [errorMsg, setErrorMsg] = useState("");

  // ---- helpers ----
  const getPaymentType = (o) =>
    Number(
      o?.order_paytype ??
      o?.order_paymenttype ??
      o?.paymentType ??
      o?.paymentTypeId ??
      0
    );

  const calcOrderTotalFromProducts = (o) => {
    const products = o?.products || [];
    const totalMrp = products.reduce((sum, item) => sum + Number(item.mrp || 0) * Number(item.quantity || 0), 0);
    const totalDiscount = products.reduce((sum, item) => {
      const qty = Number(item.quantity || 0);
      const mrp = Number(item.mrp || 0);
      const sale = Number(item.sale_price || 0);
      const prodisc = Number(item.orderlist_prodisc || 0);
      const d = prodisc > 0 ? (prodisc * qty) : ((mrp - sale) * qty);
      return sum + (Number.isFinite(d) ? d : 0);
    }, 0);
    return totalMrp - totalDiscount;
  };

  const fetchAll = async (oid) => {
    const orderIdNum = Number(oid);
    if (!orderIdNum) {
      setErrorMsg("Please enter a valid Order ID");
      setOrder(null);
      setDetails(null);
      return;
    }

    setLoading(true);
    setErrorMsg("");
    try {
      // 1) Order details (OMS)
      const o = await omsApi.getOrderById(orderIdNum);
      const orderRow = o?.data?.[0] || null;
      setOrder(orderRow);

      // 2) Razorpay payment details + refunds
      const r = await refundApi.getOrderPaymentInfo(orderIdNum);
      setDetails(r?.data?.data || null);

      // update URL query (so refresh keeps orderId)
      setSp({ orderId: String(orderIdNum) });
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.response?.data?.message || e?.message || "Failed to fetch order/payment details");
      setOrder(null);
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // auto fetch if orderId already in query
  useEffect(() => {
    if (qpOrderId) fetchAll(qpOrderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Derived values -----
  const paymentType = getPaymentType(order);

  const txn = details?.txn || details?.paymentTxn || null;

  const orderTotal = order
    ? (Number(order?.order_totalamnt ?? order?.order_total_amount ?? order?.orderAmount ?? 0) || calcOrderTotalFromProducts(order))
    : 0;

  const amountPaise = txn ? Number(txn.amountPaise ?? txn.amount_paise ?? 0) : 0;
  const refundedPaise = txn
    ? Number(txn.refundedAmountPaise ?? txn.refunded_amount_paise ?? txn.refund_amount_paise ?? 0)
    : 0;

  const paidAmount = amountPaise > 0 ? amountPaise / 100 : orderTotal;
  const refundedAmount = refundedPaise / 100;
  const remaining = Math.max(paidAmount - refundedAmount, 0);

  const isOnlinePaid = paymentType === 4; // ✅ your rule
  // const hasRazorpayTxn = !!txn && !!txn.pgPaymentId;
  // const canRefund = isOnlinePaid && hasRazorpayTxn && remaining > 0;

  const hasPaymentTransaction =
    Boolean(
      txn &&
      txn.pgPaymentId &&
      ["razorpay", "payu"].includes(
        String(
          txn.pgName || ""
        ).toLowerCase()
      )
    );

  const providerName =
    String(
      txn?.pgName || ""
    ).toUpperCase();

  const canRefund =
    isOnlinePaid &&
    hasPaymentTransaction &&
    remaining > 0;

  const accessData = JSON.parse(localStorage.getItem("accessData") || "{}");
  const roleId = Number(accessData?.roleId);
  const ALLOWED_REFUND_ROLE_IDS = [1, 2, 8, 10];
  const isRefundRoleAllowed = ALLOWED_REFUND_ROLE_IDS.includes(roleId);
  if (!isRefundRoleAllowed) {
    return (
      <Card className="p-3">
        <Alert variant="danger" className="mb-0">
          You don’t have permission to create online payment refunds.
        </Alert>
      </Card>
    );
  }
  // ----- Submit -----
  const submit = async () => {
    setErrorMsg("");

    if (!selectedOrderId) return setErrorMsg("Order ID is required");
    if (!order) return setErrorMsg("Please fetch the order details first");
    if (!isOnlinePaid) return setErrorMsg("Refund allowed only for Online Paid orders (Payment Type 4)");
    if (!hasPaymentTransaction) {
      return setErrorMsg(
        "Online payment transaction not found for this order"
      );
    }
    if (remaining <= 0) return setErrorMsg("No refundable amount remaining");

    if (refundType === "PARTIAL") {
      const a = Number(amount);
      if (!Number.isFinite(a) || a <= 0) return setErrorMsg("Enter a valid partial amount");
      if (a > remaining) return setErrorMsg(`Partial amount cannot exceed remaining ₹${remaining.toFixed(2)}`);
    }

    try {
      setLoading(true);
      const response = await refundApi.createRefund({
        orderId: Number(selectedOrderId),
        refundType,
        amount: refundType === "PARTIAL" ? Number(amount) : undefined,
        reason,
        notes: JSON.parse(notes || "{}"),
      });

      if (response?.data?.status !== "SUCCESS") {
        throw new Error(response?.data?.message || "Refund requires recheck. Open the refund list before retrying.");
      }
      navigate("/admin/payments/refunds");
    } catch (e) {
      console.error(e);
      setErrorMsg(e?.response?.data?.message || e?.message || "Refund create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-3">
      <div className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0"> Online Payment Refund</h5>
        <Badge bg={isOnlinePaid ? "success" : "secondary"}>
          PaymentType: {paymentType || "-"}
        </Badge>
      </div>

      <hr />

      {/* Order selector */}
      <Row className="g-3 align-items-end">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Order ID</Form.Label>
            <Form.Control
              value={selectedOrderId}
              onChange={(e) => setSelectedOrderId(e.target.value)}
              placeholder="Enter Order ID"
            />
          </Form.Group>
        </Col>

        <Col md="auto">
          <Button
            variant="primary"
            disabled={loading || !selectedOrderId}
            onClick={() => fetchAll(selectedOrderId)}
          >
            Fetch
          </Button>
        </Col>
      </Row>

      {errorMsg && (
        <Alert variant="danger" className="mt-3 mb-0">
          {errorMsg}
        </Alert>
      )}

      {/* Summary */}
      {order && (
        <div className="mt-3">
          <Row className="g-3">
            <Col md={3}>
              <div className="text-muted">Order Amount</div>
              <div className="fw-bold">₹ {orderTotal.toFixed(2)}</div>
            </Col>

            <Col md={3}>
              <div className="text-muted">Paid Amount</div>
              <div className="fw-bold">₹ {paidAmount.toFixed(2)}</div>
            </Col>

            <Col md={3}>
              <div className="text-muted">Refunded</div>
              <div className="fw-bold">₹ {refundedAmount.toFixed(2)}</div>
            </Col>

            <Col md={3}>
              <div className="text-muted">Remaining</div>
              <div className="fw-bold">₹ {remaining.toFixed(2)}</div>
            </Col>
          </Row>

          {txn && (
            <div className="mt-2 text-muted" style={{ fontSize: 13 }}>
              <div><b>Txn ID:</b> {txn.id} &nbsp; | &nbsp; <b>Status:</b> {txn.status}</div>
              <div>
                <b>Provider:</b>{" "}
                {providerName || "-"}
              </div>

              <div>
                <b>Provider Payment ID:</b>{" "}
                {txn.pgPaymentId || "-"}
              </div>

              <div>
                <b>Provider Status:</b>{" "}
                {txn.providerStatus || "-"}
              </div>
            </div>
          )}

          {!isOnlinePaid && (
            <Alert variant="warning" className="mt-3">
              This order is not Online Paid (PaymentType must be 4). Refund is disabled.
            </Alert>
          )}
        </div>
      )}

      <hr />

      {/* Refund Form */}
      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Refund Type</Form.Label>
            <Form.Select value={refundType} onChange={(e) => setRefundType(e.target.value)}>
              <option value="FULL">FULL</option>
              <option value="PARTIAL">PARTIAL</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Amount (₹) (only partial)</Form.Label>
            <Form.Control
              value={amount}
              disabled={refundType !== "PARTIAL"}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={refundType === "PARTIAL" ? `Max ₹${remaining.toFixed(2)}` : ""}
            />
          </Form.Group>
        </Col>

        <Col md={4}>
          <Form.Group>
            <Form.Label>Reason</Form.Label>
            <Form.Control value={reason} onChange={(e) => setReason(e.target.value)} />
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mt-3">
        <Form.Label>Notes (JSON)</Form.Label>
        <Form.Control as="textarea" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Form.Group>

      <div className="mt-3 d-flex gap-2">
        <Button variant="primary" disabled={loading || !canRefund} onClick={submit}>
          Create Refund
        </Button>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>

      {!canRefund && order && (
        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          Refund is enabled only when Payment Type is 4, a valid online payment transaction exists, and a refundable balance is available.
        </div>
      )}
    </Card>
  );
}

